import { DateTime } from 'luxon';

import { statsConfigSelectors } from '../redux/ducks/config';
import { playersAndTeamsDefaults } from '../redux/ducks/playersAndTeams';
import { dataTableDefaults, dataTableSelectors } from '../redux/ducks/playersAndTeams/dataTable';
import { filterDefaults } from '../redux/ducks/playersAndTeams/filter';
import { currentReportDefaults } from '../redux/ducks/playersAndTeams/report/current';

import { REGULAR_SEASON_ID } from './constants';
import getSortObjForCol from './getSortObjForCol';

/* getSortState - tries to create the data structure to represent sort state from query params
 * if sort params are missing or invalid, derive the sort state based on the report
 * @param {Object} config - an obj which has goalie, player, and team report data
 * @param {Object} stateMap - key/value pair representing state from query params
 * @param {Object} reportState - the updated report state obj with all needed params filled
 * @returns {Object} - A representation of the dataTable state
 */
const getSortState = (config, stateMap, reportState) => {
    const isThereSortParam = Object.keys(stateMap).some((key) => key === 'sort');
    const reportData = statsConfigSelectors.getTimeSpecificDataOnReport(config, reportState);
    const defaultReportSorting = dataTableSelectors.getDefaultSort(reportState, reportData);

    // If there is a sort param, convert it into the proper data structure and return that in an obj
    if (isThereSortParam) {
        // We map then filter, b/c sort params may contain a_ or d_ for direction, and this is taken care of
        const reportSorting = stateMap.sort
            .split(',')
            .map((col) => getSortObjForCol(col))
            .filter((col) => reportData.displayItems.includes(col.id));

        return reportSorting.length > 0 ? reportSorting : defaultReportSorting;
    }

    // else we need to create one based on the given report
    return defaultReportSorting;
};

/* getFilterValuesMap - takes an object and creates an object representing the filter.values state
 * @param {Object} stateMap - key/value pair representing state from query params
 * @returns {Object} - A representation of the filter.values state
 */
const numberFilterParams = ['isRookie', 'draftRound', 'draftYear'];
const getFilterValuesMap = (stateMap) =>
    Object.keys(stateMap)
        .filter((key) => Object.keys(filterDefaults.values).includes(key))
        .reduce((obj, key) => {
            // Parsing values into their appropriate types
            let value = stateMap[key];
            const isValueNumber = !Number.isNaN(Number(value));
            if (key === 'playerSearch') {
                value = decodeURIComponent(value);
            } else if (isValueNumber && key !== 'isRookie') {
                value = Number(value);
            } else if (!isValueNumber && numberFilterParams.includes(key)) {
                value = '';
            }
            return { ...obj, [key]: value };
        }, {});

/* getReportState - takes an object and creates an object representing the report.current state
 * @param {Object} stateMap - key/value pair representing state from query params
 * @returns {Object} - A representation of the report.current state
 */
const shortenedReportParams = [
    'aggregate',
    'gameType',
    'report',
    'reportType',
    'playerPlayedFor',
    'playerPlayedAgainst',
    'dateFromSeason',
    'position',
];
const getReportState = (stateMap) =>
    Object.keys(stateMap)
        .filter((key) => Object.keys(currentReportDefaults).includes(key) || shortenedReportParams.includes(key))
        .reduce((obj, param) => {
            let key = param;
            let value = Number.isNaN(Number(stateMap[param])) ? stateMap[param] : Number(stateMap[param]);

            switch (param) {
                case 'aggregate':
                    key = 'isAggregate';
                    value = Boolean(value);
                    break;
                case 'gameType':
                    key = 'gameTypeId';
                    value = !Number.isNaN(Number(value)) ? value : REGULAR_SEASON_ID;
                    break;
                case 'report':
                    key = 'reportName';
                    break;
                case 'reportType':
                    key = 'timeFrame';
                    break;
                case 'playerPlayedFor':
                    key = 'franchiseId';
                    break;
                case 'playerPlayedAgainst':
                    key = 'opponent';
                    break;
                case 'position':
                    key = 'positionCode';
                    break;
                case 'dateFromSeason':
                    key = 'isDateRangeFromSeasons';
                    value = true;
                    break;
                default:
                    break;
            }

            return { ...obj, [key]: value };
        }, {});

/* addMissingReportDetails - adds missing key/value pairs to a report state obj
 * Why would we need to add more? because seasons & dates mirror each other and the query params only send one range
 * If a invalid combination of seasons or dates are passed in, we will return the current season/date combination
 * @param {Object} stateMap - key/value pair representing state from query params
 * @returns {Object} - A fuller representation of the report.current state
 */
const addMissingReportDetails = (config, state) => {
    const baseReport = statsConfigSelectors.getReport(config);
    const timeFrameReportData = statsConfigSelectors.getTimeFrameReportData(
        state.timeFrame || baseReport.timeFrame,
        config.seasonData,
    );

    // This is where we'll store our additions to merge with the original obj
    let additions = {
        seasonFrom: baseReport.seasonFrom,
        seasonTo: baseReport.seasonTo,
        dateFrom: baseReport.dateFrom,
        dateTo: baseReport.dateTo,
    };

    // Check if this report exist
    let defaultReportName = '';
    if (!statsConfigSelectors.getReportData(config, state.reportName)) {
        additions.reportName = baseReport.reportName;
        defaultReportName = baseReport.reportName;
    }

    // If we are missing seasonFrom/To or dateFrom/To or only have 1 of the values from the pair, return a valid report state
    const dateFromToStateExist = state.dateFrom && state.dateTo;
    const seasonFromToStateExist = state.seasonFrom && state.seasonTo;
    if (!dateFromToStateExist && !seasonFromToStateExist) {
        return { ...baseReport, ...state, ...additions, ...timeFrameReportData };
    }

    // If dateFrom/To is missing, add them
    if (!state.dateFrom || !state.dateTo) {
        const { gameTypeId, seasonFrom, seasonTo } = state;
        const range = { from: seasonFrom, to: seasonTo };
        const seasonsMap = statsConfigSelectors.getSeasonsForRange(config, range);

        if (seasonsMap[seasonFrom] && seasonsMap[seasonTo]) {
            const dateFrom =
                !gameTypeId || gameTypeId === REGULAR_SEASON_ID
                    ? seasonsMap[seasonFrom].startDate
                    : seasonsMap[seasonFrom].regularSeasonEndDate;
            const dateTo =
                !gameTypeId || gameTypeId === REGULAR_SEASON_ID
                    ? seasonsMap[seasonTo].regularSeasonEndDate
                    : seasonsMap[seasonTo].endDate;

            const today = DateTime.local();
            const isDateToInFuture = DateTime.fromISO(dateTo) > today;

            additions = {
                dateFrom,
                dateTo: isDateToInFuture ? today.toISODate() : dateTo,
            };
        }
    }

    // If dateFrom/To is missing, add them
    if (!state.seasonFrom || !state.seasonTo) {
        const { dateFrom, dateTo } = state;
        const seasonFrom = statsConfigSelectors.getSeasonFromDate(dateFrom);
        const seasonTo = statsConfigSelectors.getSeasonFromDate(dateTo);

        if (seasonFrom && seasonTo) {
            additions = {
                seasonFrom,
                seasonTo,
            };
        }
    }

    if (defaultReportName) {
        additions.reportName = defaultReportName;
    }
    return { ...baseReport, ...state, ...additions, ...timeFrameReportData };
};

/* getDefaultState - generates the initial state tree to use as a basis
 *   Generates report information  based on the current page
 *   Generates sort data based on the report
 * @returns {Object} - object representing initial state tree
 */
const getDefaultState = (config) => {
    const report = statsConfigSelectors.getReport(config);
    const reportData = statsConfigSelectors.getTimeSpecificDataOnReport(config, report);
    const sort = dataTableSelectors.getDefaultSort(report, reportData);

    return {
        playersAndTeams: {
            ...playersAndTeamsDefaults,
            dataTable: {
                ...dataTableDefaults,
                sort,
            },
            filter: filterDefaults,
            report: {
                // Setting applied & current the same initially
                applied: {
                    ...currentReportDefaults,
                    ...report,
                },
                current: {
                    ...currentReportDefaults,
                    ...report,
                },
            },
        },
    };
};

const combineFilters = (fromQueryParams, { resultFilters }) => {
    const customFilters = fromQueryParams.filter((x) => resultFilters.includes(x.measure));
    return [...filterDefaults.values.customFilters, ...customFilters];
};

const getStateForStatsPages = (config, queryParamsList, stateMapFromQueryParams) => {
    // Custom filter params show as "filter=goals,gte,1" so we need to parse them out differently
    const customFiltersFromParams = queryParamsList
        .filter((str) => str.includes('filter='))
        .map((str) => {
            const filterDescription = str.slice(str.indexOf('=') + 1);
            const [measure, operator, value] = filterDescription.split(',');
            return { measure, operator, value };
        });

    // Create objects representing the state tree from the query params
    const filterValuesState = getFilterValuesMap(stateMapFromQueryParams);
    const reportStateFromParams = getReportState(stateMapFromQueryParams);
    const reportState = addMissingReportDetails(config, reportStateFromParams);
    const sort = getSortState(config, stateMapFromQueryParams, reportState);
    const page = stateMapFromQueryParams.page ? parseInt(stateMapFromQueryParams.page) : dataTableDefaults.page;
    const pageSize = stateMapFromQueryParams.pageSize
        ? parseInt(stateMapFromQueryParams.pageSize)
        : dataTableDefaults.pageSize;

    const customFilters = combineFilters(
        customFiltersFromParams,
        statsConfigSelectors.getTimeSpecificDataOnReport(config, reportState),
    );

    return {
        playersAndTeams: {
            ...playersAndTeamsDefaults,
            dataTable: {
                ...dataTableDefaults,
                sort,
                page,
                pageSize,
            },
            filter: {
                isVisible: false,
                values: {
                    ...filterDefaults.values,
                    ...filterValuesState,
                    customFilters,
                },
            },
            report: {
                // Setting applied & current the same initially
                applied: {
                    ...currentReportDefaults,
                    ...reportState,
                },
                current: {
                    ...currentReportDefaults,
                    ...reportState,
                },
            },
        },
    };
};

export default (config, location = window.location) => {
    /* The current report is dependent on the page the user is on and data from the server
     * The server data consists of the current game type, season range, and date range
     * We need to set up the current report with valid data in order to fetch data.
     * We'll use whatever the current season is as a basis and modify it based on query params
     */
    const defaultState = getDefaultState(config);

    if (!location.search) {
        return defaultState;
    }

    // Take the string representing the app's state and turn it into a map (key/value pairs)
    const queryParamsList = location.search.slice(1).split('&');
    const stateMapFromQueryParams = queryParamsList
        .filter((str) => !str.includes('filter='))
        .reduce((obj, str) => {
            const [key, value] = str.split('=');

            return { ...obj, [key]: value };
        }, {});

    return location.pathname === '/stats/'
        ? defaultState
        : getStateForStatsPages(config, queryParamsList, stateMapFromQueryParams);
};
